import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  Grid,
  Fab,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ColHeader } from '../pages/ClosurePayrollDataPage'; 
import { formatMoney } from '../utils/moneyFormat';

// ─── Estilos y Componentes Internos Especializados ──────────────────────────

/** Tag estilizado para la lista de conceptos (Fondo sólido) */
function ConceptTag({ type, ...props }) {
  const isBono = type === 'bono';
  return (
    <Box
      {...props}
      sx={{
        px: 1.5,
        py: 0.8,
        // Usamos colores sólidos (Verde esmeralda y Rojo intenso)
        bgcolor: isBono ? '#10B981' : '#EF4444', 
        borderRadius: '33px',
        textAlign: 'center',
        minWidth: 110,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)', // Pequeña sombra para que resalte
        ...props.sx,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: 'white', // Texto blanco para contraste perfecto
          textTransform: 'uppercase',
          lineHeight: 1,
          letterSpacing: '0.5px'
        }}
      >
        {isBono ? 'Bono' : 'Descuento'}
      </Typography>
    </Box>
  );
}

/** Interruptor deslizable (Toy Switch) interactivo en palabras y botón */
function SlidingSwitch({ isBono, onChange, disabled }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {/* Etiqueta DESC. */}
      <Typography 
        onClick={disabled ? undefined : () => onChange(false)}
        sx={{ 
          fontSize: 14, 
          fontWeight: 800, 
          // Si NO es bono (es descuento), se pinta de rojo. Si es bono, queda gris.
          color: !isBono ? '#EF4444' : '#94A3B8',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'color 0.3s ease',
        }}
      >
        DESC.
      </Typography>
      
      {/* Switch visual */}
      <Box
        onClick={disabled ? undefined : () => onChange(!isBono)}
        sx={{
          width: 54,
          height: 28,
          borderRadius: '14px',
          bgcolor: isBono ? '#10B981' : '#EF4444', // Verde o Rojo sólido
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.3s ease',
          opacity: disabled ? 0.7 : 1,
          boxShadow: 'inset 0px 2px 4px rgba(0,0,0,0.2)',
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            bgcolor: 'white',
            borderRadius: '50%',
            position: 'absolute',
            top: 3,
            left: isBono ? 29 : 3,
            transition: 'left 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
          }}
        />
      </Box>

      {/* Etiqueta BONO */}
      <Typography 
        onClick={disabled ? undefined : () => onChange(true)}
        sx={{ 
          fontSize: 14, 
          fontWeight: 800, 
          // Si ES bono, se pinta de verde. Si no, queda gris.
          color: isBono ? '#10B981' : '#94A3B8',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'color 0.3s ease',
        }}
      >
        BONO
      </Typography>
    </Stack>
  );
}


function AmountInput({ value, onChange, disabled }) {
  const handleChange = (e) => {
    const val = e.target.value;
    const regex = /^\d{0,8}(\.\d{0,2})?$/;
    if (val === '' || regex.test(val)) {
      onChange(val);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.8,
        bgcolor: 'white',
        borderRadius: '20px',
        border: '1.5px solid',
        borderColor: 'secondary.300',
        width: '100%',
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'primary.600', flexShrink: 0 }}>
        $
      </Typography>
      <TextField
        variant="standard"
        type="text" 
        inputMode="decimal"
        placeholder="0.00"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        inputProps={{
          style: { textAlign: 'right', fontWeight: 700, fontSize: 15, width: '100%' },
        }}
        sx={{
          flex: 1,
          '& .MuiInput-underline:before': { borderBottom: 'none' },
          '& .MuiInput-underline:after': { borderBottom: 'none' },
          '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
        }}
      />
    </Box>
  );
}

/** Panel blanco estilizado */
function ConceptPanel({ children, sx, disabled, ...props }) {
  return (
    <Box
      {...props}
      sx={{
        p: 2,
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'neutral.200',
        opacity: disabled ? 0.7 : 1,
        transition: 'all 0.1s ease-in-out',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const EXISTING_GRID_COLS = 'minmax(180px, 2.5fr) minmax(200px, 3.5fr) 140px 48px';

// ─── COMPONENTE PRINCIPAL MODAL ─────────────────────────────────────────────

export default function ClosureEmployeeBalanceModal({
  open,
  onClose,
  initialConcepts,
  fullName,
  onSave,
  saving,
  readOnly = false,
}) {
  const [concepts, setConcepts] = useState(() => []);
  
  const [draftName, setDraftName] = useState('');
  const [draftAmount, setDraftAmount] = useState('');
  const [isDraftBono, setIsDraftBono] = useState(true);

  useEffect(() => {
    if (open) {
      const mapped = (Array.isArray(initialConcepts) ? initialConcepts : []).map((c, i) => ({
        id: Date.now() + i,
        name: c.name,
        description: c.description ?? c.descripcion ?? '',
        amount: Number(c.amount),
        type: c.type,
      }));
      setConcepts(mapped);
      resetDraft();
    }
  }, [open, initialConcepts]);

  const resetDraft = () => {
    setDraftName('');
    setDraftAmount('');
    setIsDraftBono(true);
  };

  const currentTotal = useMemo(() => {
    return concepts.reduce((sum, c) => {
      const val = Number(c.amount || 0);
      return c.type === 'descuento' ? sum - val : sum + val;
    }, 0);
  }, [concepts]);
  const bonusTotal = useMemo(
    () => concepts
      .filter((concept) => concept.type !== 'descuento')
      .reduce((sum, concept) => sum + Number(concept.amount || 0), 0),
    [concepts],
  );
  const discountTotal = useMemo(
    () => concepts
      .filter((concept) => concept.type === 'descuento')
      .reduce((sum, concept) => sum + Number(concept.amount || 0), 0),
    [concepts],
  );

  const handleAddConcept = () => {
    if (!draftName || !draftAmount || isNaN(Number(draftAmount)) || Number(draftAmount) <= 0) return;
    
    setConcepts((prev) => [...prev, {
      id: Date.now(),
      name: draftName,
      amount: Number(draftAmount),
      type: isDraftBono ? 'bono' : 'descuento',
    }]);
    resetDraft();
  };

  const handleDeleteConcept = (id) => {
    setConcepts((prev) => prev.filter((c) => c.id !== id));
  };

  const handleFinalSave = () => {
    const cleanConcepts = concepts.map(({ name, description, amount, type }) => ({
      name,
      ...(description ? { description } : {}),
      amount,
      type,
    }));
    onSave?.(cleanConcepts);
  };

  const isDraftValid = draftName && draftAmount && Number(draftAmount) > 0;

  return (
    <Dialog
      open={open}
      onClose={saving && !readOnly ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'neutral.100', 
          borderRadius: '24px',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        {readOnly
          ? <VisibilityIcon sx={{ color: 'primary.900', fontSize: 24 }} />
          : <EditIcon sx={{ color: 'primary.900', fontSize: 24 }} />}
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'primary.900', flex: 1 }}>
          {readOnly ? 'Detalle de balance' : 'Editar Balance'} - {fullName || 'Empleado'}
        </Typography>
        <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: 'neutral.500' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 4, display: 'flex', flexDirection: 'column', gap: 3, minHeight: 0 }}>
        
        {/* Conceptos Actuales */}
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
          <ColHeader sx={{ justifyContent: 'start', px: 2 }}>Conceptos Actuales</ColHeader>

          {concepts.length === 0 ? (
            <ConceptPanel disabled={saving} sx={{ textAlign: 'center', py: 3 }}>
              <Typography sx={{ fontSize: 14, color: 'neutral.600', fontWeight: 600 }}>
                {readOnly
                  ? 'No se registraron bonos ni descuentos.'
                  : 'No hay conceptos cargados. Agrega uno nuevo abajo.'}
              </Typography>
            </ConceptPanel>
          ) : (
            concepts.map((concept) => {
              const isBono = concept.type === 'bono';
              return (
                <ConceptPanel
                  key={concept.id}
                  disabled={saving}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: readOnly
                      ? 'minmax(260px, 1fr) 160px'
                      : EXISTING_GRID_COLS,
                    gap: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                    <ConceptTag type={concept.type} sx={{ py: 0.5, px: 1, minWidth: 100 }} />
                    <Box minWidth={0}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.900' }}>
                        {concept.name}
                      </Typography>
                      {concept.description && (
                        <Typography sx={{ mt: 0.25, fontSize: 12, color: 'neutral.600' }}>
                          {concept.description}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, right: readOnly ? 0 : -30, color: 'primary.900', textAlign: 'right' }}>
                    {formatMoney(isBono ? concept.amount : -concept.amount)}
                  </Typography>
                  {!readOnly && (
                    <IconButton onClick={() => handleDeleteConcept(concept.id)} disabled={saving} size="small" sx ={{right: -30}}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </ConceptPanel>
              );
            })
          )}
        </Stack>

        {/* Resumen Total Conceptos */}
        <Box sx={{ p: 2, bgcolor: 'secondary.200', borderRadius: '16px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'neutral.600' }}>Bonos</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'success.700' }}>
              {formatMoney(bonusTotal)}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'neutral.600' }}>Descuentos</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'error.700' }}>
              {formatMoney(discountTotal)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'neutral.600' }}>Balance neto</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'primary.900'}}>
              {formatMoney(currentTotal)}
            </Typography>
          </Box>
        </Box>

        {/* Nuevo Concepto */}
        {!readOnly && (
        <Stack spacing={2} sx={{ flexShrink: 0 }}>
          <ColHeader sx={{ justifyContent: 'start', px: 2 }}>Nuevo Concepto de Balance</ColHeader>

          <Box sx={{ position: 'relative' }}>
            <ConceptPanel disabled={saving} sx={{ p: 3, pr: 10 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <SlidingSwitch isBono={isDraftBono} onChange={setIsDraftBono} disabled={saving} />
                </Grid>
                
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    label="Nombre Concepto"
                    placeholder="Ej: Bono presentismo"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    disabled={saving}
                    InputProps={{ style: { fontWeight: 700 } }}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '12px' } }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <AmountInput value={draftAmount} onChange={setDraftAmount} disabled={saving} />
                </Grid>
              </Grid>
            </ConceptPanel>

            <Fab
              color="warning"
              onClick={handleAddConcept}
              disabled={!isDraftValid || saving}
              sx={{
                position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)',
                bgcolor: 'warning.400', color: 'primary.900',
                '&:hover': { bgcolor: 'warning.500' },
                '&:disabled': { opacity: 0.6, bgcolor: 'warning.200' },
              }}
            >
              <AddIcon fontSize="large" />
            </Fab>
          </Box>
        </Stack>
        )}
      </DialogContent>

      <Stack direction="row" sx={{ p: 2, px: 3, pb: 3, justifyContent: 'flex-end', gap: 2, flexShrink: 0 }}>
        <Button onClick={onClose} disabled={saving && !readOnly} variant="outlined" sx={{ textTransform: 'none', borderRadius: '24px', px: 4, py: 1, color: 'primary.900', borderColor: 'neutral.400', fontWeight: 700 }}>
          {readOnly ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!readOnly && (
          <Button onClick={handleFinalSave} disabled={saving} variant="contained" color="primary" sx={{ textTransform: 'none', borderRadius: '24px', px: 4, py: 1, fontWeight: 700, display: 'flex', gap: 1 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar Balance'}
          </Button>
        )}
      </Stack>
    </Dialog>
  );
}
